import React, { useEffect, useMemo, useState, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import LineChart from 'apollo-react/components/LineChart';
import ClusterColumnChart from 'apollo-react/components/ClusterColumnChart';
import BarChart from 'apollo-react/components/BarChart';
import DonutChart from 'apollo-react/components/DonutChart';
import ListChart from 'apollo-react/components/ListChart';
import axiosInstance from '../../axiosInstance';
import {
  buildDashboardDateQueryParams,
  dashboardDateFilterKey,
} from '../../utils/dashboardDateRangeUtils';
import Typography from 'apollo-react/components/Typography';
import {
  EMPTY_CLUSTER_COLUMN_CHART_DATA,
  EMPTY_LINE_CHART_DATA,
  EMPTY_PAYMENT_CLUSTER_COLUMN_CHART_DATA,
  EMPTY_PAYMENT_LINE_CHART_DATA,
  HOME_CLUSTER_COLUMN_CHART_DATA,
  HOME_LINE_CHART_DATA,
  HOME_PAYMENT_CLUSTER_COLUMN_CHART_DATA,
  HOME_PAYMENT_LINE_CHART_CURRENCY,
  HOME_PAYMENT_LINE_CHART_DATA,
} from './homeDashboardStaticData';
import {
  getCalendarDaysUntilLicenseEnd,
  isHcpLicenseExpired,
  isHcpLicenseExpiringWithin30DaysNotExpired,
  renewalBand,
} from '../../utils/hcpLicenseRenewalDays';
import {
  isEventManagementMode,
  isLicenseTrackingMode,
  useLicensingExperienceOptional,
} from '../../contexts/LicensingExperienceContext';
import ColoredBars, {
  BAR_COLORS,
  ColoredClusters,
} from './ColoredBars/ColoredBars';
import TenderRequestsModal, {
  TENDER_LIST_STATUS,
  TENDER_STATUS_BY_LABEL,
} from './TenderCharts/TenderRequestsModal';
import styles from './HomeDashboardCharts.module.css';

/** Primary dashboard charts (shared grid item style for LT / EM / default). */
export const homePageGridItemStyle = {
  width: '100%',
  minWidth: 0,
  margin: 0,
  padding: 0,
  border: '0px',
  boxSizing: 'border-box',
};

const CHART_ROW_HEIGHT = 380;
const BLUEPRINT_CHART_HEIGHT = 280;

/** Draw Succeeded last so its line stays visible when values match Failed. */
const PAYMENT_LINE_SERIES_ORDER = ['Failed', 'Pending', 'Succeeded'];
const PAYMENT_LINE_CHART_COLORS = [
  BAR_COLORS.red,
  BAR_COLORS.grey,
  BAR_COLORS.green,
];
const HOME_LINE_CHART_COLORS = [
  BAR_COLORS.blue,
  BAR_COLORS.green,
  BAR_COLORS.grey,
];
const CLUSTER_SERIES_COLORS = [BAR_COLORS.blue, BAR_COLORS.green];

function sortPaymentLineSeries(seriesList) {
  if (!Array.isArray(seriesList) || seriesList.length === 0) return seriesList;
  const byLabel = new Map(seriesList.map((series) => [series.label, series]));
  const ordered = PAYMENT_LINE_SERIES_ORDER.map((label) =>
    byLabel.get(label)
  ).filter(Boolean);
  return ordered.length > 0 ? ordered : seriesList;
}

const slaListColumns = [
  { header: 'Request ID', accessor: 'requestId' },
  { header: 'Workflow', accessor: 'workflow' },
  { header: 'Days left', accessor: 'daysLeft', align: 'right' },
  { header: 'Priority', accessor: 'priority' },
];

const hcpLicenseListColumns = [
  { header: 'Applicant / user', accessor: 'applicant' },
  { header: 'Emirates ID', accessor: 'emiratesId' },
  { header: 'Days to renewal', accessor: 'daysLeft', align: 'right' },
];

function hcpLicenseBasePath(persona) {
  if (persona === 'enduser') return '/enduser';
  // Super-admin uses the same registry routes as admin
  return '/admin';
}

function HcpLicenseRenewalLists({ persona }) {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);

  useEffect(() => {
    const url =
      persona === 'enduser'
        ? '/api/hcp-licenses-list/enduser'
        : '/api/hcp-licenses-list';
    let cancelled = false;

    axiosInstance
      .get(url, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setLicenses(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setLicenses([]);
      });

    return () => {
      cancelled = true;
    };
  }, [persona]);

  const toRows = (rows) =>
    rows.slice(0, 25).map((row) => ({
      applicant:
        row?.end_user?.username ||
        row?.end_user?.email ||
        row?.sheet_end_user_email ||
        '—',
      emiratesId:
        row?.hcp_emirates_id != null ? String(row.hcp_emirates_id) : '—',
      daysLeft: String(
        getCalendarDaysUntilLicenseEnd(row.license_end_date) ?? '—'
      ),
      onClick: () =>
        navigate(
          `${hcpLicenseBasePath(persona)}/hcp-licenses-list/details/${row.id}`
        ),
    }));

  const groups = [
    {
      title: 'Expired Licenses',
      rows: toRows(licenses.filter((row) => isHcpLicenseExpired(row))),
      emptyMessage: 'No expired licenses.',
    },
    {
      title: 'Licenses will expire in < 30 days',
      rows: toRows(
        licenses.filter((row) =>
          isHcpLicenseExpiringWithin30DaysNotExpired(row)
        )
      ),
      emptyMessage: 'No licenses in this window.',
    },
    {
      title: 'Licenses will expire in 31 – 60 days',
      rows: toRows(licenses.filter((row) => renewalBand(row, '60'))),
      emptyMessage: 'No licenses in this window.',
    },
    {
      title: 'Licenses will expire in 61 – 90 days',
      rows: toRows(licenses.filter((row) => renewalBand(row, '90'))),
      emptyMessage: 'No licenses in this window.',
    },
  ];

  return (
    <div className={styles.blueprintGrid}>
      {groups.map((group) => (
        <div className={styles.blueprintPanel} key={group.title}>
          <div style={homePageGridItemStyle}>
            <Card tabIndex={0}>
              <CardHeader title={group.title} />
              <CardContent style={{ paddingTop: 0 }}>
                <ListChart
                  columns={hcpLicenseListColumns}
                  rows={group.rows}
                  height={280}
                  emptyMessage={group.emptyMessage}
                  stripedRows
                />
              </CardContent>
            </Card>
          </div>
        </div>
      ))}
    </div>
  );
}

function isValidLineChartData(data) {
  if (!Array.isArray(data) || data.length === 0) return false;
  const pointCount = data[0]?.data?.length;
  if (!pointCount) return false;

  const referenceXValues = data[0].data.map((point) => String(point?.x));
  if (referenceXValues.some((x) => x === 'undefined' || x === 'null')) {
    return false;
  }

  return data.every(
    (series) =>
      series?.label &&
      Array.isArray(series.data) &&
      series.data.length === pointCount &&
      series.data.every(
        (point, index) =>
          point?.x != null &&
          String(point.x) === referenceXValues[index] &&
          Number.isFinite(Number(point.y))
      )
  );
}

function isValidClusterChartData(data) {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    data.every(
      (row) =>
        row?.label &&
        Array.isArray(row.data) &&
        row.data.length > 0 &&
        typeof row.data[0] === 'object'
    )
  );
}

/**
 * Apollo d3 charts attach mouse handlers in componentDidMount. Defer mount until
 * layout is ready and data is final to avoid hover on detached SVG nodes.
 */
function DeferredChartMount({
  children,
  minHeight = 280,
  mountClassName = '',
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setMounted(true));
    });
    return () => {
      window.cancelAnimationFrame(frame);
      setMounted(false);
    };
  }, []);

  if (!mounted) {
    return (
      <div
        className={styles.chartPlaceholder}
        style={{ minHeight, height: minHeight }}
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      className={`${styles.chartMount} ${mountClassName}`.trim()}
      style={{ minHeight, height: minHeight }}
    >
      {children}
    </div>
  );
}

/** Apollo BarChart requires at least one row; crashes on empty `data`. */
const TENDER_PIPELINE_BAR_COLORS = [
  BAR_COLORS.blue, // Submitted
  BAR_COLORS.grey, // Pending
  BAR_COLORS.green, // Awarded
  BAR_COLORS.red, // Lost
];
const STATUS_BAR_COLORS = [
  BAR_COLORS.blue, // Assigned
  BAR_COLORS.grey, // In progress
  BAR_COLORS.green, // Approved
  BAR_COLORS.red, // Rejected
];
const PRIORITY_BAR_COLORS = [
  BAR_COLORS.red, // Critical
  BAR_COLORS.blue, // High
  BAR_COLORS.blue, // Medium
  BAR_COLORS.blue, // Low
];

function normalizeBarChartData(rows, fallbackLabels) {
  if (Array.isArray(rows) && rows.length > 0) {
    return rows.map((row) => ({
      label: String(row.label ?? '—'),
      value: Number(row.value) || 0,
    }));
  }
  return fallbackLabels.map((label) => ({ label, value: 0 }));
}

function totalAtLineIndex(seriesList, i) {
  let sum = 0;
  for (const s of seriesList) {
    const y = Number(s?.data?.[i]?.y);
    sum += Number.isFinite(y) ? y : 0;
  }
  return sum;
}

function filterLineSeriesNoAllZeroMonths(seriesList) {
  if (!Array.isArray(seriesList) || seriesList.length === 0) return seriesList;
  const first = seriesList[0]?.data;
  if (!Array.isArray(first) || first.length === 0) return seriesList;
  const n = first.length;
  const keepIdx = [];
  for (let i = 0; i < n; i += 1) {
    const sum = totalAtLineIndex(seriesList, i);
    if (sum > 0) {
      keepIdx.push(i);
      continue;
    }
    if (i < n - 1 && totalAtLineIndex(seriesList, i + 1) > 0) {
      keepIdx.push(i);
    }
  }
  if (keepIdx.length === 0) {
    const last = n - 1;
    return seriesList.map((s) => ({
      ...s,
      data: s.data?.length ? [s.data[last]] : [],
    }));
  }
  return seriesList.map((s) => ({
    ...s,
    data: keepIdx.map((idx) => s.data[idx]),
  }));
}

function clusterRowTotal(row) {
  const block = row?.data?.[0];
  if (!block || typeof block !== 'object') return 0;
  return Object.values(block).reduce((acc, v) => acc + (Number(v) || 0), 0);
}

function filterClusterNoAllZeroBars(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const totals = rows.map(clusterRowTotal);
  const filtered = rows.filter((_, j) => {
    if (totals[j] > 0) return true;
    if (j >= rows.length - 1) return false;
    return totals[j + 1] > 0;
  });
  if (filtered.length > 0) return filtered;
  return rows.length ? [rows[rows.length - 1]] : [];
}

/** Remap home-charts cluster series for Event Management labeling. */
function remapClusterToEventManagement(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const block = row?.data?.[0] || {};
    return {
      label: row.label,
      data: [
        {
          'Event requests':
            Number(block['New submissions'] ?? block['Event requests'] ?? 0) ||
            0,
          'Completed requests': Number(block['Completed requests'] ?? 0) || 0,
        },
      ],
    };
  });
}

function sumEventClusterTotals(rows) {
  let eventRequests = 0;
  let completedRequests = 0;
  for (const row of rows || []) {
    const block = row?.data?.[0] || {};
    eventRequests += Number(block['Event requests']) || 0;
    completedRequests += Number(block['Completed requests']) || 0;
  }
  return { eventRequests, completedRequests };
}

/**
 * Event Management metrics panel: live volume chart + date-range totals.
 *
 * @param {{
 *   clusterData?: Array,
 *   chartsReady?: boolean,
 *   chartInstanceId?: string,
 *   persona?: string,
 * }} props
 */
export function HomeEventManagementDashboardPanel({
  clusterData = [],
  chartsReady = false,
  chartInstanceId = 'em',
  persona = 'admin',
}) {
  const eventClusterDisplay = useMemo(() => {
    const remapped = remapClusterToEventManagement(clusterData);
    const filtered = filterClusterNoAllZeroBars(remapped);
    if (!isValidClusterChartData(filtered) || filtered.length === 0) {
      return remapClusterToEventManagement(EMPTY_CLUSTER_COLUMN_CHART_DATA);
    }
    return filtered;
  }, [clusterData]);

  const { eventRequests, completedRequests } = useMemo(
    () => sumEventClusterTotals(eventClusterDisplay),
    [eventClusterDisplay]
  );

  const displayEventRequests = chartsReady ? eventRequests : '—';
  const displayCompleted = chartsReady ? completedRequests : '—';

  return (
    <div className={styles.chartPanel}>
      <div className={styles.chartPanelInner}>
        <Card tabIndex={0} className={styles.chartCard}>
          <CardHeader title="Quarterly volume — Event requests vs completed" />
          <CardContent className={styles.chartCardContent}>
            <div className={styles.emIndicatorRow}>
              <div className={styles.emIndicatorCard}>
                <Typography
                  variant="bodySmall"
                  emphasis="low"
                  className={styles.emIndicatorLabel}
                >
                  Event requests
                </Typography>
                <Typography className={styles.emIndicatorValue}>
                  {displayEventRequests}
                </Typography>
              </div>
              <div className={styles.emIndicatorCard}>
                <Typography
                  variant="bodySmall"
                  emphasis="low"
                  className={styles.emIndicatorLabel}
                >
                  Completed requests
                </Typography>
                <Typography className={styles.emIndicatorValue}>
                  {displayCompleted}
                </Typography>
              </div>
            </div>
            {chartsReady ? (
              <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                <ColoredClusters colors={CLUSTER_SERIES_COLORS}>
                  <ClusterColumnChart
                    key={`${chartInstanceId}-em-cluster-${persona}`}
                    data={eventClusterDisplay}
                    yTicks={6}
                    height={CHART_ROW_HEIGHT}
                  />
                </ColoredClusters>
              </DeferredChartMount>
            ) : (
              <div
                className={styles.chartPlaceholder}
                style={{
                  minHeight: CHART_ROW_HEIGHT,
                  height: CHART_ROW_HEIGHT,
                }}
                aria-hidden="true"
              />
            )}
            <Typography
              variant="bodySmall"
              emphasis="low"
              style={{ marginTop: 12 }}
            >
              Totals and chart reflect the selected dashboard date range.
            </Typography>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   persona: 'admin' | 'super-admin' | 'enduser',
 *   dateFilter?: { preset: string, customRange: any },
 *   statusBarData?: Array<{ label: string, value: number }>,
 *   priorityBarData?: Array<{ label: string, value: number }>,
 *   approvalRatePercent?: number | null,
 *   slaAtRiskRows?: Array<object>,
 *   showBlueprintCharts?: boolean,
 *   showPaymentCharts?: boolean,
 *   summaryReady?: boolean,
 * }} props
 */
export function HomeDashboardChartsSection({
  persona,
  dateFilter,
  statusBarData = [],
  priorityBarData = [],
  approvalRatePercent = null,
  slaAtRiskRows = [],
  showBlueprintCharts = true,
  showPaymentCharts = false,
  summaryReady = true,
}) {
  const navigate = useNavigate();
  const { mode } = useLicensingExperienceOptional();
  const chartInstanceId = useId();
  const [chartsReady, setChartsReady] = useState(false);
  const [lineData, setLineData] = useState(HOME_LINE_CHART_DATA);
  const [clusterData, setClusterData] = useState(
    HOME_CLUSTER_COLUMN_CHART_DATA
  );
  const [paymentLineData, setPaymentLineData] = useState(
    HOME_PAYMENT_LINE_CHART_DATA
  );
  const [paymentLineCurrency, setPaymentLineCurrency] = useState(
    HOME_PAYMENT_LINE_CHART_CURRENCY
  );
  const [paymentClusterData, setPaymentClusterData] = useState(
    HOME_PAYMENT_CLUSTER_COLUMN_CHART_DATA
  );
  const [paymentChartsEnabled, setPaymentChartsEnabled] =
    useState(showPaymentCharts);
  const [showHcpLicenseUi, setShowHcpLicenseUi] = useState(false);
  const [tenderCharts, setTenderCharts] = useState(null);
  const [tenderModalStatus, setTenderModalStatus] = useState(null);
  const showLicenseTrackingDashboard =
    showHcpLicenseUi && isLicenseTrackingMode(mode);
  const showEventManagementDashboard = isEventManagementMode(mode);

  const lineDisplay = useMemo(() => {
    const filtered = filterLineSeriesNoAllZeroMonths(lineData);
    return isValidLineChartData(filtered) ? filtered : HOME_LINE_CHART_DATA;
  }, [lineData]);

  const clusterDisplay = useMemo(() => {
    const filtered = filterClusterNoAllZeroBars(clusterData);
    if (!isValidClusterChartData(filtered) || filtered.length === 0) {
      return HOME_CLUSTER_COLUMN_CHART_DATA;
    }
    return filtered;
  }, [clusterData]);

  const paymentLineDisplay = useMemo(() => {
    const filtered = filterLineSeriesNoAllZeroMonths(paymentLineData);
    const candidate = isValidLineChartData(filtered)
      ? filtered
      : HOME_PAYMENT_LINE_CHART_DATA;
    return sortPaymentLineSeries(candidate);
  }, [paymentLineData]);

  const paymentClusterDisplay = useMemo(() => {
    const remapped = (
      Array.isArray(paymentClusterData) ? paymentClusterData : []
    ).map((row) => {
      const block = row?.data?.[0] || {};
      return {
        label: row.label,
        data: [
          {
            'Threshold reached':
              Number(
                block['Threshold reached'] ?? block['Succeeded payments'] ?? 0
              ) || 0,
            'Threshold not reached':
              Number(
                block['Threshold not reached'] ?? block['Failed payments'] ?? 0
              ) || 0,
          },
        ],
      };
    });
    const filtered = filterClusterNoAllZeroBars(remapped);
    if (!isValidClusterChartData(filtered) || filtered.length === 0) {
      return HOME_PAYMENT_CLUSTER_COLUMN_CHART_DATA;
    }
    return filtered;
  }, [paymentClusterData]);

  const dateFilterKey = useMemo(
    () => dashboardDateFilterKey(dateFilter),
    [dateFilter]
  );

  const slaRows = useMemo(
    () =>
      slaAtRiskRows.map((row) => ({
        requestId: row.requestId,
        workflow: row.workflow,
        daysLeft: row.daysLeft,
        priority: row.priority,
        onClick: row.path ? () => navigate(row.path) : undefined,
      })),
    [slaAtRiskRows, navigate]
  );

  const statusBarDisplay = useMemo(
    () =>
      normalizeBarChartData(statusBarData, [
        'Assigned',
        'In progress',
        'Approved',
        'Rejected',
      ]),
    [statusBarData]
  );

  const tenderPipelineDisplay = useMemo(
    () =>
      normalizeBarChartData(tenderCharts?.pipeline, [
        'Submitted',
        'Pending',
        'Awarded',
        'Lost',
      ]),
    [tenderCharts]
  );

  const tenderWinRate = tenderCharts?.winRate;

  // Apollo's BarChart has no click callback; its d3 bars (and axis labels) carry
  // the row / label as DOM data, so read it from the click target.
  const handleTenderPipelineClick = (event) => {
    const datum = event.target?.__data__;
    const label = typeof datum === 'string' ? datum : datum?.label;
    const status = TENDER_STATUS_BY_LABEL[label];
    if (status) setTenderModalStatus(status);
  };

  const priorityBarDisplay = useMemo(
    () =>
      normalizeBarChartData(priorityBarData, [
        'Critical',
        'High',
        'Medium',
        'Low',
      ]),
    [priorityBarData]
  );

  useEffect(() => {
    const isCustomer = persona === 'enduser';
    const url = isCustomer
      ? '/api/dashboard/home-charts/enduser'
      : '/api/dashboard/home-charts';
    const fallbackLine = isCustomer
      ? EMPTY_LINE_CHART_DATA
      : HOME_LINE_CHART_DATA;
    // Event Management: never fall back to demo cluster volumes.
    const fallbackCluster =
      isCustomer || isEventManagementMode(mode)
        ? EMPTY_CLUSTER_COLUMN_CHART_DATA
        : HOME_CLUSTER_COLUMN_CHART_DATA;
    let cancelled = false;
    setChartsReady(false);

    const params = buildDashboardDateQueryParams(dateFilter);

    axiosInstance
      .get(url, { params, withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        if (isValidLineChartData(res.data?.lineChartData)) {
          setLineData(res.data.lineChartData);
        } else {
          setLineData(fallbackLine);
        }
        if (isValidClusterChartData(res.data?.clusterChartData)) {
          setClusterData(res.data.clusterChartData);
        } else {
          setClusterData(fallbackCluster);
        }
        setPaymentChartsEnabled(res.data?.showPaymentCharts === true);
        setShowHcpLicenseUi(res.data?.showHcpLicenseUi === true);
        setTenderCharts(
          res.data?.tenderCharts?.enabled ? res.data.tenderCharts : null
        );
        if (res.data?.showPaymentCharts === true) {
          if (isValidLineChartData(res.data?.paymentLineChartData)) {
            setPaymentLineData(res.data.paymentLineChartData);
            setPaymentLineCurrency(
              res.data?.paymentLineCurrency || HOME_PAYMENT_LINE_CHART_CURRENCY
            );
          } else {
            setPaymentLineData(EMPTY_PAYMENT_LINE_CHART_DATA);
            setPaymentLineCurrency(HOME_PAYMENT_LINE_CHART_CURRENCY);
          }
          if (isValidClusterChartData(res.data?.paymentClusterChartData)) {
            setPaymentClusterData(res.data.paymentClusterChartData);
          } else {
            setPaymentClusterData(EMPTY_PAYMENT_CLUSTER_COLUMN_CHART_DATA);
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLineData(fallbackLine);
        setClusterData(fallbackCluster);
        setPaymentChartsEnabled(false);
        setShowHcpLicenseUi(false);
        setTenderCharts(null);
      })
      .finally(() => {
        if (!cancelled) setChartsReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [persona, dateFilterKey, dateFilter, mode]);

  return (
    <div className={styles.chartsSection}>
      <div className={styles.primaryChartsRow}>
        <div className={styles.chartPanel}>
          <div className={styles.chartPanelInner}>
            <Card tabIndex={0} className={styles.chartCard}>
              <CardHeader
                title={
                  showEventManagementDashboard
                    ? 'Event requests & review throughput'
                    : 'Requests & review throughput'
                }
              />
              <CardContent
                style={{
                  flex: 1,
                  width: '100%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {chartsReady ? (
                  <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                    <LineChart
                      key={`${chartInstanceId}-line-${persona}`}
                      data={lineDisplay}
                      dataColors={HOME_LINE_CHART_COLORS}
                      xTicks={5}
                      height={CHART_ROW_HEIGHT}
                    />
                  </DeferredChartMount>
                ) : (
                  <div
                    className={styles.chartPlaceholder}
                    style={{
                      minHeight: CHART_ROW_HEIGHT,
                      height: CHART_ROW_HEIGHT,
                    }}
                    aria-hidden="true"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {showEventManagementDashboard ? (
          <HomeEventManagementDashboardPanel
            clusterData={clusterData}
            chartsReady={chartsReady}
            chartInstanceId={chartInstanceId}
            persona={persona}
          />
        ) : (
          <div className={styles.chartPanel}>
            <div className={styles.chartPanelInner}>
              <Card tabIndex={0} className={styles.chartCard}>
                <CardHeader
                  title={
                    showLicenseTrackingDashboard
                      ? 'Quarterly volume — Licenses requests vs HCP registry'
                      : 'Monthly volume — submissions vs completed'
                  }
                />
                <CardContent
                  style={{
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {chartsReady ? (
                    <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                      <ColoredClusters colors={CLUSTER_SERIES_COLORS}>
                        <ClusterColumnChart
                          key={`${chartInstanceId}-cluster-${persona}`}
                          data={clusterDisplay}
                          yTicks={6}
                          height={CHART_ROW_HEIGHT}
                        />
                      </ColoredClusters>
                    </DeferredChartMount>
                  ) : (
                    <div
                      className={styles.chartPlaceholder}
                      style={{
                        minHeight: CHART_ROW_HEIGHT,
                        height: CHART_ROW_HEIGHT,
                      }}
                      aria-hidden="true"
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {paymentChartsEnabled && showPaymentCharts ? (
        <div className={styles.primaryChartsRow}>
          <div className={styles.chartPanel}>
            <div className={styles.chartPanelInner}>
              <Card tabIndex={0} className={styles.chartCard}>
                <CardHeader title="Spendings over time" />
                <CardContent
                  style={{
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {chartsReady ? (
                    <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                      <LineChart
                        key={`${chartInstanceId}-payment-line-${persona}`}
                        data={paymentLineDisplay}
                        dataColors={PAYMENT_LINE_CHART_COLORS}
                        suffix={` ${paymentLineCurrency}`}
                        xTicks={5}
                        height={CHART_ROW_HEIGHT}
                      />
                    </DeferredChartMount>
                  ) : (
                    <div
                      className={styles.chartPlaceholder}
                      style={{
                        minHeight: CHART_ROW_HEIGHT,
                        height: CHART_ROW_HEIGHT,
                      }}
                      aria-hidden="true"
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className={styles.chartPanel}>
            <div className={styles.chartPanelInner}>
              <Card tabIndex={0} className={styles.chartCard}>
                <CardHeader title="Monthly volume — Threshold reached" />
                <CardContent
                  style={{
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {chartsReady ? (
                    <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                      <ColoredClusters colors={CLUSTER_SERIES_COLORS}>
                        <ClusterColumnChart
                          key={`${chartInstanceId}-payment-cluster-${persona}`}
                          data={paymentClusterDisplay}
                          yTicks={6}
                          height={CHART_ROW_HEIGHT}
                        />
                      </ColoredClusters>
                    </DeferredChartMount>
                  ) : (
                    <div
                      className={styles.chartPlaceholder}
                      style={{
                        minHeight: CHART_ROW_HEIGHT,
                        height: CHART_ROW_HEIGHT,
                      }}
                      aria-hidden="true"
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : null}

      {chartsReady && tenderCharts ? (
        <div className={`${styles.blueprintGrid} ${styles.blueprintGridTwo}`}>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="Tender pipeline" />
                <CardContent className={styles.blueprintCardContent}>
                  <div
                    className={styles.clickableChart}
                    onClick={handleTenderPipelineClick}
                    role="presentation"
                    title="Click a bar to list its requests"
                  >
                    <DeferredChartMount minHeight={BLUEPRINT_CHART_HEIGHT}>
                      <ColoredBars colors={TENDER_PIPELINE_BAR_COLORS}>
                        <BarChart
                          key={`${chartInstanceId}-tender-pipeline-${persona}`}
                          data={tenderPipelineDisplay}
                          height={BLUEPRINT_CHART_HEIGHT}
                        />
                      </ColoredBars>
                    </DeferredChartMount>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="Win rate" />
                <CardContent
                  className={`${styles.blueprintCardContent} ${styles.blueprintCardContentCenter}`}
                >
                  <div
                    className={styles.clickableChart}
                    onClick={() =>
                      setTenderModalStatus(TENDER_LIST_STATUS.WIN_RATE)
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        setTenderModalStatus(TENDER_LIST_STATUS.WIN_RATE);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    title="Click to list the tenders used in the win rate"
                  >
                    <DeferredChartMount
                      minHeight={BLUEPRINT_CHART_HEIGHT}
                      mountClassName={styles.approvalDonutMount}
                    >
                      <DonutChart
                        stroke={BAR_COLORS.blue}
                        className={styles.approvalDonutChart}
                        percent={tenderWinRate?.percent ?? 0}
                        subtitle="Win rate"
                        tooltipTitle="Win rate"
                        tooltipSubtitle={
                          tenderWinRate?.percent != null
                            ? `${tenderWinRate.won} won of ${tenderWinRate.decided} decided (${tenderWinRate.percent}%)`
                            : 'No decided tenders yet'
                        }
                      />
                    </DeferredChartMount>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : null}

      {tenderCharts ? (
        <TenderRequestsModal
          status={tenderModalStatus}
          winRate={tenderWinRate}
          dateFilter={dateFilter}
          persona={persona}
          onClose={() => setTenderModalStatus(null)}
        />
      ) : null}

      {showBlueprintCharts && summaryReady ? (
        <div className={styles.blueprintGrid}>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="Request status distribution" />
                <CardContent className={styles.blueprintCardContent}>
                  <DeferredChartMount minHeight={BLUEPRINT_CHART_HEIGHT}>
                    <ColoredBars colors={STATUS_BAR_COLORS}>
                      <BarChart
                        key={`${chartInstanceId}-status-bar-${persona}`}
                        data={statusBarDisplay}
                        height={BLUEPRINT_CHART_HEIGHT}
                      />
                    </ColoredBars>
                  </DeferredChartMount>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="Approval rate" />
                <CardContent
                  className={`${styles.blueprintCardContent} ${styles.blueprintCardContentCenter}`}
                >
                  <DeferredChartMount
                    minHeight={BLUEPRINT_CHART_HEIGHT}
                    mountClassName={styles.approvalDonutMount}
                  >
                    <DonutChart
                      stroke={BAR_COLORS.blue}
                      className={styles.approvalDonutChart}
                      percent={approvalRatePercent ?? 0}
                      subtitle="Approved"
                      tooltipTitle="Approval rate"
                      tooltipSubtitle={
                        approvalRatePercent != null
                          ? `${approvalRatePercent}%`
                          : 'No data yet'
                      }
                    />
                  </DeferredChartMount>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="SLA priority breakdown" />
                <CardContent className={styles.blueprintCardContent}>
                  <DeferredChartMount minHeight={BLUEPRINT_CHART_HEIGHT}>
                    <ColoredBars colors={PRIORITY_BAR_COLORS}>
                      <BarChart
                        key={`${chartInstanceId}-priority-bar-${persona}`}
                        data={priorityBarDisplay}
                        height={BLUEPRINT_CHART_HEIGHT}
                      />
                    </ColoredBars>
                  </DeferredChartMount>
                </CardContent>
              </Card>
            </div>
          </div>
          <div
            className={`${styles.blueprintPanel} ${styles.blueprintFullWidth}`}
          >
            <div style={homePageGridItemStyle}>
              <Card tabIndex={0}>
                <CardHeader title="SLA at risk & overdue" />
                <CardContent style={{ paddingTop: 0 }}>
                  <ListChart
                    columns={slaListColumns}
                    rows={slaRows}
                    height={BLUEPRINT_CHART_HEIGHT}
                    emptyMessage="No requests at SLA risk."
                    stripedRows
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : null}
      {showLicenseTrackingDashboard ? (
        <HcpLicenseRenewalLists persona={persona} />
      ) : null}
    </div>
  );
}
